import { AlertTriangle, CheckCircle, FileText, Upload, X } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { API_BASE_URL } from '../lib/api';

export const MerchantAppealTestPage: React.FC = () => {
    const { user } = useMerchantAuth();
    const [transactionId, setTransactionId] = useState('');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async () => {
        if (!transactionId) return;

        if (files.length === 0) {
            setError('Для создания апелляции необходимо прикрепить файл подтверждения');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('transaction_id', transactionId);
            if (description) {
                formData.append('description', description);
            }
            files.forEach((file) => formData.append('files', file));

            const response = await fetch(`${API_BASE_URL}/api/merchant/appeals`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${user?.api_key}`,
                },
                body: formData
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || 'Ошибка при создании апелляции');
            }

            setResult(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFilesSelected = (selected: FileList | null) => {
        if (!selected) return;
        const next = [...files, ...Array.from(selected)];
        if (next.length > 10) {
            setError('Можно прикрепить не более 10 файлов');
            return;
        }
        const oversized = next.find((item) => item.size > 10 * 1024 * 1024);
        if (oversized) {
            setError(`Файл «${oversized.name}» превышает лимит 10 МБ`);
            return;
        }
        setError(null);
        setFiles(next);
    };

    return (
        <div className="min-w-0">
            <div className="max-w-2xl mx-auto space-y-6">
                <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-slate-100">Тест Апелляций</h2>
                    <p className="text-gray-500 dark:text-slate-400">Создание спора по транзакции (API)</p>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-orange-600 dark:text-orange-300">
                            <AlertTriangle className="h-5 w-5" />
                            Открыть спор
                        </CardTitle>
                        <CardDescription>
                            Введите ID транзакции (из истории платежей), по которой возникла проблема.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label>ID Транзакции</Label>
                            <Input
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value.trim())}
                                placeholder="Например: 65a1b2c3d4e5f6..."
                                className="font-mono"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Описание проблемы</Label>
                            <Textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Опишите причину спора..."
                                rows={4}
                                className="resize-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Файл подтверждения (Чек/Скриншот) <span className="text-red-500">*</span></Label>
                            <div className="border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg p-6 flex flex-col items-center justify-center hover:bg-gray-50 dark:hover:bg-slate-900 transition-colors">
                                <Input
                                    type="file"
                                    multiple
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => {
                                        handleFilesSelected(e.target.files);
                                        e.target.value = '';
                                    }}
                                    className="hidden"
                                    id="file-upload"
                                />
                                <Label
                                    htmlFor="file-upload"
                                    className="cursor-pointer flex flex-col items-center gap-2"
                                >
                                    {files.length > 0 ? (
                                        <>
                                            <FileText className="h-8 w-8 text-blue-500" />
                                            <span className="text-sm font-medium text-gray-900 dark:text-slate-100">Выбрано файлов: {files.length}</span>
                                            <span className="text-xs text-gray-500 dark:text-slate-400">Нажмите, чтобы добавить ещё</span>
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="h-8 w-8 text-gray-400" />
                                            <span className="text-sm font-medium text-gray-600 dark:text-slate-300">Нажмите для выбора файлов</span>
                                            <span className="text-xs text-gray-400">До 10 файлов по 10 МБ</span>
                                        </>
                                    )}
                                </Label>
                            </div>
                            {files.map((item, index) => (
                                <div key={`${item.name}-${item.lastModified}-${index}`} className="mt-2 flex items-center justify-between rounded border p-2 text-sm">
                                    <span className="truncate">{item.name}</span>
                                    <Button type="button" variant="ghost" size="sm" onClick={() => setFiles(files.filter((_, i) => i !== index))}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>

                        <Button
                            onClick={handleSubmit}
                            disabled={loading || !transactionId || files.length === 0}
                            className="w-full bg-orange-600 hover:bg-orange-700"
                        >
                            {loading ? 'Отправка...' : 'Создать апелляцию'}
                        </Button>
                    </CardContent>
                </Card>

                {error && (
                    <div className="p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-300 mt-0.5" />
                        <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
                    </div>
                )}

                {result && (
                    <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-3 mb-2">
                                <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-300" />
                                <h3 className="font-semibold text-green-900 dark:text-green-200">Апелляция успешно создана!</h3>
                            </div>
                            <div className="pl-9 space-y-1 text-sm text-green-800 dark:text-green-200">
                                <p>ID Апелляции: <span className="font-mono font-bold">{result.appeal_id}</span></p>
                                <p>Статус: <span className="uppercase">{result.status}</span></p>
                                <p className="text-xs mt-2 text-green-700 dark:text-green-300 opacity-80">Webhook уведомление отправлено.</p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
};


